/**
 * Chat Sharing API client.
 *
 * Pre-cut this module talked to express's /api/chat/:id/share* and
 * /api/shared/:token REST endpoints. Post-cut everything routes through
 * sabaody to shanks's mono.collab.v1.CollabService — same business
 * rules (max direct shares, same-tenant link check, owner-only writes),
 * same response shape on the wire, just Connect instead of REST.
 *
 * The exported `sharingApi` surface is unchanged so component-level
 * callers (ShareDialog, SharedWithMeList, ResolveTokenPage) don't need
 * to know the transport switched.
 */ function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { Code, ConnectError } from '@connectrpc/connect';
import { CollabTypes } from '@mono/proto-clients';
import { authClient, collabClient } from '../connect/clients';
const _API_URL = process.env.REACT_APP_API_URL || '';
/** API error with status code */ export class SharingApiError extends Error {
    constructor(message, status){
        super(message), _define_property(this, "status", void 0), this.status = status;
        this.name = 'SharingApiError';
    }
}
// Map Connect Codes to HTTP-like statuses. Callers branch on
// SharingApiError.status using 403/404/410; preserve that contract.
function connectCodeToStatus(code) {
    switch(code){
        case Code.NotFound:
            return 404;
        case Code.PermissionDenied:
        case Code.Unauthenticated:
            return 403;
        case Code.FailedPrecondition:
            return 410;
        case Code.InvalidArgument:
            return 400;
        default:
            return 500;
    }
}
// Convert any ConnectError thrown by `op` into a SharingApiError so
// the calling component's existing error UI keeps working. Non-Connect
// errors flow through unchanged (network failures etc.).
async function asSharingApi(op, fallback) {
    try {
        return await op();
    } catch (err) {
        if (err instanceof ConnectError) {
            throw new SharingApiError(err.message || fallback, connectCodeToStatus(err.code));
        }
        throw err;
    }
}
function bareChatId(chatId) {
    return chatId.startsWith('shanks-') ? chatId.slice(7) : chatId;
}
function linkExpirationToProto(v) {
    switch(v){
        case '7d':
            return CollabTypes.LinkExpiration.LINK_EXPIRATION_7D;
        case '30d':
            return CollabTypes.LinkExpiration.LINK_EXPIRATION_30D;
        case '90d':
            return CollabTypes.LinkExpiration.LINK_EXPIRATION_90D;
        default:
            return CollabTypes.LinkExpiration.LINK_EXPIRATION_NEVER;
    }
}
function tsToISO(ts) {
    return ts ? ts.toDate().toISOString() : '';
}
function nullableTsToISO(ts) {
    return ts ? ts.toDate().toISOString() : null;
}
export const sharingApi = {
    /**
   * Get sharing settings for a chat (owner only).
   */ async getShareSettings (chatId) {
        return asSharingApi(async ()=>{
            const resp = await collabClient.getShareSettings({
                chatId: bareChatId(chatId)
            });
            return {
                isOwner: resp.isOwner,
                directShares: resp.directShares.map((d)=>({
                        shareId: d.shareId,
                        userId: d.userId,
                        email: d.email,
                        name: d.name,
                        createdAt: tsToISO(d.createdAt)
                    })),
                linkShare: resp.linkShare ? {
                    shareId: resp.linkShare.shareId,
                    enabled: resp.linkShare.enabled,
                    expiresAt: nullableTsToISO(resp.linkShare.expiresAt),
                    url: resp.linkShare.url
                } : undefined,
                limits: {
                    maxDirectShares: resp.limits?.maxDirectShares ?? 0,
                    currentDirectShares: resp.limits?.currentDirectShares ?? 0
                }
            };
        }, 'Failed to fetch share settings');
    },
    /**
   * Share a chat with users and/or create a share link.
   */ async shareChat (chatId, request) {
        return asSharingApi(async ()=>{
            const resp = await collabClient.shareConversation({
                chatId: bareChatId(chatId),
                users: (request.users ?? []).map((u)=>({
                        userId: u.userId,
                        email: u.email,
                        name: u.name,
                        companyId: ''
                    })),
                createLink: !!request.createLink,
                linkExpiration: linkExpirationToProto(request.linkExpiration)
            });
            // Filter direct shares from the union response. The wire surface
            // is `repeated ShareSummary` (link + direct flattened) — the
            // existing TS interface separates them: direct in `shares[]`,
            // link in `shareLink`.
            const directShares = resp.shares.filter((s)=>s.shareType === CollabTypes.ShareType.DIRECT).map((s)=>({
                    shareId: s.shareId,
                    userId: s.sharedWithUserId,
                    email: s.sharedWithEmail,
                    name: s.sharedWithName,
                    createdAt: tsToISO(s.createdAt)
                }));
            return {
                shares: directShares,
                shareLink: resp.shareLink || undefined,
                errors: resp.errors.map((e)=>({
                        userId: e.userId,
                        reason: e.reason
                    })),
                notifications: {
                    attempted: resp.notificationsAttempted,
                    sent: resp.notificationsQueued,
                    failed: 0
                }
            };
        }, 'Failed to share chat');
    },
    /**
   * Revoke a specific share.
   */ async revokeShare (chatId, shareId) {
        await asSharingApi(()=>collabClient.revokeShare({
                chatId: bareChatId(chatId),
                shareId
            }), 'Failed to revoke share');
    },
    /**
   * Revoke all shares for a chat. There is no bulk-revoke RPC in
   * CollabService (express had no bulk handler either — this method
   * was previously a 404). Surfaces as a clear error rather than
   * silently no-op so the caller can decide between iterating or
   * surfacing to the user.
   */ async revokeAllShares (_chatId) {
        throw new SharingApiError('revokeAllShares is not implemented; call revokeShare per share id instead', 501);
    },
    /**
   * Enable or disable link sharing.
   */ async toggleLink (chatId, enabled) {
        await asSharingApi(()=>collabClient.toggleLinkShare({
                chatId: bareChatId(chatId),
                enabled
            }), 'Failed to update link');
    },
    /**
   * Regenerate the share link (invalidates the old link).
   */ async regenerateLink (chatId) {
        return asSharingApi(async ()=>{
            const resp = await collabClient.rotateShareLink({
                chatId: bareChatId(chatId)
            });
            return {
                url: resp.url
            };
        }, 'Failed to regenerate link');
    },
    /**
   * Get chats shared with the current user.
   */ async getSharedWithMe (page = 1, limit = 20, search, sortBy) {
        return asSharingApi(async ()=>{
            const resp = await collabClient.listSharedWithMe({
                page,
                limit,
                search: search ?? '',
                sortBy: sortBy === 'title' ? 'title' : ''
            });
            return {
                chats: resp.chats.map((c)=>({
                        chatId: c.chatId,
                        title: c.title,
                        sharedBy: {
                            userId: c.sharedByUserId,
                            name: c.sharedByName,
                            email: c.sharedByEmail
                        },
                        sharedAt: tsToISO(c.sharedAt),
                        lastMessage: c.lastMessagePreview || c.lastMessageRole ? {
                            role: c.lastMessageRole || 'assistant',
                            preview: c.lastMessagePreview,
                            createdAt: tsToISO(c.lastMessageCreatedAt)
                        } : undefined
                    })),
                pagination: {
                    total: resp.pagination?.total ?? 0,
                    page: resp.pagination?.page ?? page,
                    limit: resp.pagination?.limit ?? limit,
                    totalPages: resp.pagination?.totalPages ?? 0
                }
            };
        }, 'Failed to fetch shared chats');
    },
    /**
   * Search for users to share with, over Connect.
   *
   * This used express's /api/user/search-for-sharing until
   * mono.auth.v1.AuthService/ListUsersForSharing was exposed through sabaody;
   * moving it is what let /api/user come off dock-one's allowlist. The handler
   * scopes results to the caller's own tenant, so no tenant id is sent.
   *
   * The slice is not redundant. The REST endpoint took a limit (10 with a
   * query, 3 without) and the RPC has no limit field — mariejois returns up to
   * 50. Trimming here keeps the picker showing what it always showed, so this
   * stays a transport change rather than a quiet UX change.
   */ async searchUsers (query) {
        const limit = query ? 10 : 3;
        const response = await authClient.listUsersForSharing({
            query
        });
        return response.users.slice(0, limit).map((user)=>({
                userId: user.id,
                email: user.email,
                name: user.name
            }));
    },
    /**
   * Resolve a share link token to chat info. Public-token path with
   * authenticated caller (sabaody's JWT validation gives us the
   * caller's tenant).
   */ async resolveToken (token) {
        return asSharingApi(async ()=>{
            const resp = await collabClient.resolveShareToken({
                token
            });
            return {
                chatId: resp.chatId,
                title: resp.title,
                sharedBy: {
                    name: resp.sharedByName
                }
            };
        }, 'This link is no longer valid');
    }
};
